import { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import * as TokenSale from '../../../../contracts/master'
import useWeb3 from '../../../../hooks/useWeb3'
import {
  selectAccount,
  selectBalanceEth,
} from '../../../../modules/web3/selectors'
import AnimatedLoader from '../../../../shared_components/AnimatedLoader'
import { Box } from '../../../../shared_components/UI/Box'
import { numberWithCommas } from '../../../../utils/commaSeperators'
import { Allocation } from '../types'
import { toast } from 'react-toastify'
import { Container, InfoSection, PsuedoButton } from './styles'
import { ThemedButton } from '../../../../shared_components/ThemedButton'
import EmbeddableModal from '../../../../shared_components/EmbeddableModal'
import HowToUseRefund from '../../HowToUseRefund'
import { IPool } from '../../../PoolDetails/Missions/types'
import styled from 'styled-components'
import { formatDateString } from '../../../PoolDetails/Details'
import { createContract } from '../../Launches'
import { useGetContractByNameQuery } from '../../../../modules/web3/api'
import { getExchangeRatePrice } from '../../../PoolDetails/SwapBox'
import useNetworkId from '../../../../hooks/useNetworkId'
import { ethers } from 'ethers'
import { improvedAmountFromWei } from '../../../../contexts/StakingContext'

export type CoreInvestmentInfo = {
  userInvestment: number
  tokensReceived: number
  refund: number
  pricePerToken: number
  investmentAfterDilution: number
  pool: IPool
  hasClaimedRefund: boolean
  isEthRaise?: boolean
  vestingBegins: Date
  hasPoolFailed?: boolean
  hasPoolFailedRefund?: boolean
}
interface Props {
  hasRefund: boolean
  allocation?: Allocation
  coreInvestmentInfo?: CoreInvestmentInfo
  poolNotEnded?: boolean
  vestingReady?: boolean
}

const InvestmentInfo: FC<Props> = ({
  hasRefund,
  allocation,
  coreInvestmentInfo,
  poolNotEnded,
  vestingReady,
}) => {
  const [isClaiming, setIsClaiming] = useState(false)
  const [hasClaimedRefund, setHasClaimedRefund] = useState(
    allocation?.hasClaimedRefund ?? coreInvestmentInfo?.hasClaimedRefund,
  )
  const [showClaimModal, setShowClaimModal] = useState(false)

  const { provider, updateBalance } = useWeb3()
  const balanceInEth = useSelector(selectBalanceEth)
  const account = useSelector(selectAccount)

  const {
    userInvestment,
    tokensReceived,
    refund,
    pricePerToken,
    investmentAfterDilution,
    pool,
    isEthRaise,
    vestingBegins,
  } = allocation ?? coreInvestmentInfo ?? {}

  const { networkId } = useNetworkId()
  const { data: tokenSaleContractData } = useGetContractByNameQuery({
    name: `TokenSale${isEthRaise ? 'ETH' : 'USDB'}`,
    chainId: networkId,
  })

  const tokenSaleAbi = tokenSaleContractData?.contract?.abi

  if (!userInvestment || !pricePerToken || !pool) return <></>

  const ticker = allocation?.pool?.ticker ?? coreInvestmentInfo?.pool?.ticker
  const hasPoolFailed = coreInvestmentInfo?.hasPoolFailed

  const claimRefund = async () => {
    try {
      const isLabsPool = pool?.isVetted
      if (!isLabsPool && !tokenSaleAbi) throw new Error('No token sale abi')
      setIsClaiming(true)
      const dynamicMaster = new ethers.Contract(
        pool?.poolAddress,
        TokenSale.abi as ethers.InterfaceAbi,
        provider,
      )
      const contract = isLabsPool
        ? dynamicMaster
        : createContract(
            tokenSaleAbi as ethers.InterfaceAbi,
            pool.poolAddress,
            provider,
            account as string,
          )

      const gas = await contract?.claim.estimateGas()

      if (!gas) throw new Error('No gas')
      if (improvedAmountFromWei(gas, 9) * 2n > BigInt(balanceInEth)) {
        toast.error('Insufficient balance for gas fees')
        throw new Error('Insufficient balance for gas fees')
      }

      await contract.claim({ gasLimit: gas })

      await updateBalance(account)

      toast.success('Refund claimed')

      setHasClaimedRefund(true)
      setShowClaimModal(false)
    } catch (e) {
      console.error(e)
      toast.error('Error claiming refund')
    } finally {
      setIsClaiming(false)
    }
  }

  const refundToDisplay = showDecimalsIfNeeded(refund, isEthRaise ? 6 : 2)

  const investmentAfterDilutionToDisplay = showDecimalsIfNeeded(
    investmentAfterDilution,
  )

  const currencyToDisplay = isEthRaise ? 'ETH' : 'USDB'
  const pricePerTokenExchange = getExchangeRatePrice({
    ticker: pool?.ticker,
    price: String(pool?.privateSale?.price),
    poolSettings: pool?.poolSettings,
  })
  const canClaimRefund = !hasClaimedRefund && !poolNotEnded && vestingReady

  const isLabsPool = pool?.isVetted

  if (hasPoolFailed) {
    return (
      <PoolFailed
        hasRefundArrived={!!coreInvestmentInfo?.hasPoolFailedRefund}
      />
    )
  }

  return (
    <Container dc>
      {(poolNotEnded || (!vestingReady && !isLabsPool)) && (
        <PoolStillLive
          awaitingVesting={!poolNotEnded && !vestingReady && !isLabsPool}
        />
      )}
      <Box ac g={12} className="inner">
        <InfoSection>
          <span className="title">Your investment</span>
          <span>
            {numberWithCommas(Number(userInvestment?.toFixed(0)) ?? 0)}{' '}
            <span className="low-opacity">{currencyToDisplay}</span>
          </span>
        </InfoSection>
        <InfoSection>
          <span className="title">Tokens allocated</span>
          <span>
            {numberWithCommas(Number(tokensReceived?.toFixed(2)) ?? 0)}{' '}
            <span className="low-opacity">${ticker}</span>
          </span>
        </InfoSection>
        {hasRefund && (
          <>
            <InfoSection>
              <Box align="center">
                <Box direction="column">
                  <span className="title">
                    {poolNotEnded ? 'Estimated ' : ' '} Refund
                    {!hasClaimedRefund && !poolNotEnded && ' Available'}
                  </span>
                  <span className={!hasClaimedRefund ? 'pink' : ''}>
                    {refundToDisplay}{' '}
                    <span className={hasClaimedRefund ? 'low-opacity' : ''}>
                      {currencyToDisplay}
                    </span>
                  </span>
                </Box>
                {hasClaimedRefund && (
                  <PsuedoButton isBlue>claimed</PsuedoButton>
                )}
              </Box>
            </InfoSection>
            <InfoSection>
              <span className="title">Investment after dilution</span>
              <span>
                {investmentAfterDilutionToDisplay}{' '}
                <span className="low-opacity">{currencyToDisplay}</span>
              </span>
            </InfoSection>
          </>
        )}
        {vestingBegins && (
          <InfoSection isLast>
            <span className="title">Vesting begins</span>
            <span>
              {formatDateString(vestingBegins.toISOString()).split(' ')[0]}
            </span>
          </InfoSection>
        )}
        <InfoSection isLast>
          <span className="title">Price</span>
          <span>{pricePerTokenExchange}</span>
        </InfoSection>
        {hasRefund && canClaimRefund && (
          <ThemedButton
            $styleType="primary"
            onClick={() => setShowClaimModal(true)}
          >
            {isClaiming ? <AnimatedLoader size={20} /> : 'Claim Refund'}
          </ThemedButton>
        )}
      </Box>
      <EmbeddableModal
        dismiss={() => {
          setShowClaimModal(false)
        }}
        isVisible={!!showClaimModal}
        hideBolt
      >
        <HowToUseRefund
          claim={claimRefund}
          investmentAmountInUsdb={userInvestment}
          tokensAfterDilution={Number(
            Number(String(investmentAfterDilution * pricePerToken)),
          )}
          totalRefund={refund}
          isClaiming={isClaiming}
          isLabs
          isEthRaise={isEthRaise}
        />
      </EmbeddableModal>
    </Container>
  )
}

export default InvestmentInfo

export function showDecimalsIfNeeded(price: number, decimalLimit = 2) {
  const priceToFixed = Number(price?.toFixed(decimalLimit)) ?? 0
  const priceSplit = priceToFixed.toString().split('.')
  const priceWhole = priceSplit[0]
  const priceDecimal = priceSplit?.[1]
  const priceToDisplay = priceWhole + (!!priceDecimal ? '.' + priceDecimal : '')

  return numberWithCommas(priceToDisplay)
}

const PoolNotLiveSpan = styled.span`
  color: ${({ theme }) => theme.colors.error};
  font-size: 14px;
  text-align: left;
  margin: 0;
  padding-left: 8px;
  padding-bottom: 12px;
  display: flex;
  gap: 8px;
  align-items: center;
  max-width: 820px;
`

function PoolFailed({ hasRefundArrived }: { hasRefundArrived: boolean }) {
  return (
    <Box marginTop={16}>
      <PoolNotLiveSpan>
        <img src="/img/icons/alert-triangle.svg" alt="warning" />
        This pool failed to reach the required minimum threshold. You{' '}
        {hasRefundArrived
          ? 'have been paid a refund'
          : 'will soon be refunded'}{' '}
        for your original investment.{' '}
        {!hasRefundArrived &&
          'This will usually be around 15 minutes after the raise completes but may be longer, but rest assured we have a record of your investment and you will be refunded shortly.'}
      </PoolNotLiveSpan>
    </Box>
  )
}

function PoolStillLive({ awaitingVesting }: { awaitingVesting?: boolean }) {
  return (
    <Box>
      <PoolNotLiveSpan>
        <img src="/img/icons/alert-triangle.svg" alt="warning" />
        Please note this pool is currently still{' '}
        {!awaitingVesting ? 'open' : 'processing'}. You will be able to access
        your tokens once{' '}
        {!awaitingVesting ? 'the pool closes' : 'this is complete'}
      </PoolNotLiveSpan>
    </Box>
  )
}
